/* DISPATCHER */
import { AppDispatcher } from 'dispatcher';
/* CONSTANTS */
import * as constants from 'vd-constants';
/* CLIENTS */
import { CallhomeApi, PlatformApi } from 'clients';
/* STORES */
import { AuthStore } from 'stores/AuthStore';
import EventFilterStore from 'stores/EventFilterStore';
import PreferenceStore from 'stores/PreferenceStore';
/* ACTIONS */
import { SEARCH_OPERATOR } from '@solinkcorp/vd-common';
import get from 'lodash/get';
import AnalyticsLogger from 'models/AnalyticsLogger';
import * as DateHandler from 'utils/DateHandler';

/**
 * @todo Replace with settings from CH
 * @type {{}}
 */
const THRESHOLD_DEFAULTS = {
  [constants.REAL_TIME_NOTIFICATION_PUSH]: constants.REAL_TIME_NOTIFICATION_THRESHOLD_PUSH,
  [constants.REAL_TIME_NOTIFICATION_SMS]: constants.REAL_TIME_NOTIFICATION_THRESHOLD_SMS,
  [constants.REAL_TIME_NOTIFICATION_EMAIL]: constants.REAL_TIME_NOTIFICATION_THRESHOLD_EMAIL,
};

export default {
  setEventTypesFilter({
    bookmark = false,
    motion = false,
    starred = false,
    transaction = false,
    showRevenue = false,
  }) {
    const eventTypes = {
      bookmark,
      motion,
      transaction,
      starred,
    };
    AppDispatcher.dispatch(constants.SET_EVENT_TYPES_FILTER, { eventTypes, showRevenue });
  },

  setEventSubtypeFilter(features) {
    AppDispatcher.dispatch(constants.SET_EVENT_SUBTYPES_FILTER, { eventSubtypes: features });
  },

  /**
   * @param {object} options
   * @param {{[eventType: string]: boolean}} options.eventTypesFilter
   * @param {{[subType: string]: boolean}} options.subtypeFilter
   * @param {string[] | undefined} options.tagsFilter
   * @returns {void}
   */
  setFeatureTabFilters({ eventTypesFilter, subtypeFilter, tagsFilter }) {
    AppDispatcher.dispatch(constants.SET_FEATURE_TAB_FILTERS, {
      eventTypesFilter,
      subtypeFilter,
      tagsFilter,
    });
  },

  clearFeatureTabFilters() {
    AppDispatcher.dispatch(constants.CLEAR_FEATURE_TAB_FILTERS, {});
  },

  setEventSubtypeFilterRevenue() {
    AppDispatcher.dispatch(constants.SET_EVENT_SUBTYPES_FILTER_REVENUE);
  },

  setRevenueFilter() {
    AppDispatcher.dispatch(constants.SET_REVENUE_FILTER);
  },

  toggleRevenueFilter() {
    AppDispatcher.dispatch(constants.TOGGLE_REVENUE_FILTER);
  },

  resetFilters() {
    AppDispatcher.dispatch(constants.RESET_EVENT_FILTERS, {});
  },

  /**
   *
   * @param {ReportUtil} [report=null]
   */
  selectFilter(report = null) {
    const locale = PreferenceStore.getLocale();
    AnalyticsLogger.selectReportOnEventsPage({ report, locale });

    AppDispatcher.dispatch(constants.SELECT_FILTER, {
      selectedReport: report,
    });
  },

  clearSelectedReport() {
    AppDispatcher.dispatch(constants.CLEAR_SELECTED_REPORT);
  },

  fetchFilters({ onlyVisible = true, onlyFilters = true } = {}, cacheRequest) {
    const userId = AuthStore.getUser()?.sub;

    if (!userId) {
      return;
    }

    return new Promise((resolve, reject) => {
      // dispatch an action,
      // in case a store might want to know while this is in progress
      // I like this approach... See:
      // http://stackoverflow.com/a/25648726/1831150
      AppDispatcher.dispatch(constants.FETCH_FILTERS, {});
      const reportType = onlyFilters ? 'filter' : undefined;

      CallhomeApi()
        .listReports({ userId, reportType, onlyVisible }, cacheRequest)
        .then((response) => {
          AppDispatcher.dispatch(constants.FETCH_FILTERS_SUCCESS, {
            payload: response,
          });
          resolve(response);
        })
        .catch((err) => {
          console.error('Error fetching reports from EventFilterStore', err);
          AppDispatcher.dispatch(constants.FETCH_FILTERS_ERROR, {
            error: err,
          });

          // for some reason the 401 isn't being propagated through.
          if (err.statusCode !== 500 && err.statusCode !== 400) {
            // AuthActions.logout();
          }
          reject(err);
        });
    });
  },

  /**
   * Create report (user assignment).
   * @param {ReportUtil} report
   * @param {boolean} [isVisible]
   * @param {boolean} [hasDigest]
   * @param {Object} [reportSettings]
   */
  createReport({ report, isVisible, hasDigest, reportSettings }) {
    const userId = AuthStore.getUser()?.sub;

    if (!userId) {
      return;
    }

    CallhomeApi()
      .createReport({
        reportTemplateId: report.getTemplateId(),
        userId,
        isVisible,
        hasDigest,
        reportSettings,
      })
      .then((response) => {
        AppDispatcher.dispatch(constants.REPORT_UPDATE_SUCCESS, {
          reportTemplateId: report.getTemplateId(),
          report: response,
        });
      })
      .catch((err) => {
        AppDispatcher.dispatch(constants.REPORT_UPDATE_ERROR, {
          error: err,
        });
      });
  },

  /**
   * Update report (user assignment).
   * @param {ReportUtil} report
   * @param {boolean} [isVisible]
   * @param {boolean} [hasDigest]
   * @param {Object} [reportSettings]
   */
  updateReport({ report, isVisible, hasDigest, reportSettings }) {
    const userId = AuthStore.getUser()?.sub;

    if (!userId) {
      return;
    }

    CallhomeApi()
      .updateReport({
        userId,
        reportId: report.getReportId(),
        isVisible,
        hasDigest,
        reportSettings,
      })
      .then((response) => {
        AppDispatcher.dispatch(constants.REPORT_UPDATE_SUCCESS, {
          reportTemplateId: report.getTemplateId(),
          report: response,
        });
      })
      .catch((err) => {
        AppDispatcher.dispatch(constants.REPORT_UPDATE_ERROR, {
          error: err,
        });
      });
  },

  /**
   * Update report template (definition).
   * @param {ReportUtil} report
   * @param {Object} data
   */
  updateReportTemplate({ report, data }) {
    const userId = AuthStore.getUser()?.sub;

    if (!userId) {
      return;
    }

    CallhomeApi()
      .updateReportTemplate({ userId, templateId: report.getTemplateId(), data })
      .then((response) => {
        AppDispatcher.dispatch(constants.REPORT_TEMPLATE_UPDATE_SUCCESS, {
          reportTemplateId: report.getTemplateId(),
          payload: response,
        });
      })
      .catch((err) => {
        AppDispatcher.dispatch(constants.REPORT_TEMPLATE_UPDATE_ERROR, {
          error: err,
        });
      });
  },

  /**
   * Delete report template (definition).
   * @param {ReportUtil} report
   */
  deleteReportTemplate({ report }) {
    const userId = AuthStore.getUser()?.sub;

    if (!userId) {
      return;
    }
    CallhomeApi()
      .deleteReportTemplate({ templateId: report.getTemplateId(), userId })
      .then((response) => {
        AppDispatcher.dispatch(constants.REPORT_TEMPLATE_DELETE_SUCCESS, {
          reportTemplateId: report.getTemplateId(),
          payload: response,
        });
      })
      .catch((err) => {
        AppDispatcher.dispatch(constants.REPORT_TEMPLATE_DELETE_ERROR, {
          error: err,
        });
      });
  },

  canEnableUserNotification({ report, notificationType, isEnabled }) {
    const canEnableNotification = EventFilterStore.getRealTimeReportCanEnable({
      reportTemplateId: report.getTemplateId(),
      notificationType,
    });

    if (canEnableNotification === undefined && isEnabled) {
      // Threshold check: determine whether the real-time notification can be enabled based on the average events returned for the report

      const days = 7;
      const range = 'Week';
      const query = get(report.getQuery(), 'query', {});
      const params = {
        date: DateHandler.subtractDaysFromDate({ days }).toISOString(),
        origin: 'start',
        range,
        timeFieldToSearchOn: 'startTime',
        ...query.bool,
      };

      return PlatformApi()
        .listHistogram(params)
        .then((response) => {
          // Determine the average number of notifications that would be generated over the period
          let totalPerDay = 0;
          const { results = [] } = response;
          for (const result of results) {
            totalPerDay += result.total || 0;
          }
          const average = totalPerDay / days;

          const canEnable = average <= THRESHOLD_DEFAULTS[notificationType];

          AppDispatcher.dispatch(constants.USER_NOTIFICATION_REAL_TIME_THRESHOLD, {
            average,
            canEnable,
            thresholdType: notificationType,
            reportTemplateId: report.getTemplateId(),
          });

          return { canChange: canEnable };
        })
        .catch((err) => {
          AppDispatcher.dispatch(constants.USER_NOTIFICATION_UPDATE_ERROR, {
            error: err,
          });
        });
    }
    // Either the real-time notification check was performed previously, or the notification is being disabled (defaulting to true for latter case)
    return new Promise((resolve) =>
      resolve({ canChange: canEnableNotification !== undefined ? canEnableNotification : true }),
    );
  },

  createUserNotification({ report, notificationType, isEnabled }) {
    const userId = AuthStore.getUser()?.sub;

    if (!userId) {
      return;
    }
    return this.canEnableUserNotification({ report, notificationType, isEnabled })
      .then(({ canChange }) => {
        if (canChange) {
          return CallhomeApi().createUserNotification({
            userId,
            reportTemplateId: report.getTemplateId(),
            notificationType,
            isEnabled,
          });
        }
        return Promise.reject(
          new Error(
            `Create User Notification Error: Exceeds real-time notification threshold [type=${notificationType}]`,
          ),
        );
      })
      .then((response) => {
        AppDispatcher.dispatch(constants.USER_NOTIFICATION_UPDATE_SUCCESS, {
          reportTemplateId: report.getTemplateId(),
          payload: response,
        });
      })
      .catch((err) => {
        AppDispatcher.dispatch(constants.USER_NOTIFICATION_UPDATE_ERROR, {
          error: err,
        });
      });
  },

  updateUserNotification({ report, userNotificationId, notificationType, isEnabled }) {
    return this.canEnableUserNotification({ report, notificationType, isEnabled })
      .then(({ canChange }) => {
        if (canChange) {
          return CallhomeApi().updateUserNotification({
            userNotificationId,
            notificationType,
            isEnabled,
          });
        }
        return Promise.reject(
          new Error(
            `Update User Notification Error: Exceeds real-time notification threshold [type=${notificationType}]`,
          ),
        );
      })
      .then((response) => {
        AppDispatcher.dispatch(constants.USER_NOTIFICATION_UPDATE_SUCCESS, {
          reportTemplateId: report.getTemplateId(),
          payload: response,
        });
      })
      .catch((err) => {
        AppDispatcher.dispatch(constants.USER_NOTIFICATION_UPDATE_ERROR, {
          error: err,
        });
      });
  },

  deleteUserNotification({ report, userNotificationId }) {
    CallhomeApi()
      .deleteUserNotification({ userNotificationId })
      .then(() => {
        AppDispatcher.dispatch(constants.USER_NOTIFICATION_DELETE_SUCCESS, {
          reportTemplateId: report.getTemplateId(),
        });
      })
      .catch((err) => {
        AppDispatcher.dispatch(constants.USER_NOTIFICATION_DELETE_ERROR, {
          error: err,
        });
      });
  },

  enableSavedFilter() {
    AppDispatcher.dispatch(constants.ENABLE_SAVED_FILTER);
  },

  resetSavedFilter() {
    AppDispatcher.dispatch(constants.RESET_SAVED_FILTER);
  },

  updateSearchOperator(operator) {
    AppDispatcher.dispatch(constants.UPDATE_SEARCH_OPERATOR, { operator });
  },

  resetSearchOperatorToDefault() {
    AppDispatcher.dispatch(constants.UPDATE_SEARCH_OPERATOR, { operator: SEARCH_OPERATOR.AND });
  },
};
